import { useEffect, useState } from 'react';

import { Icon } from '@/icon/Icon';
import { cn } from '@/utils/cn';

import type { MultipleChoiceElementProps } from '../MultipleChoice.types';

export function MultipleChoiceUnstyled(props: MultipleChoiceElementProps) {
  const { element, onChange } = props;
  const { name, value, options, description, required, custom_field_id } = element.attrs?.data || {};

  // Internal state for optimistic UI feedback
  const initialSelectedValues = Array.isArray(value) ? value : value ? [value] : [];
  const [selectedValues, setSelectedValues] = useState<string[]>(initialSelectedValues);

  // Keep internal state in sync if value prop changes
  useEffect(() => {
    const newSelectedValues = Array.isArray(value) ? value : value ? [value] : [];
    setSelectedValues(newSelectedValues);
  }, [value]);

  if (!name || !custom_field_id || !options) {
    return null;
  }

  const isMultipleChoice = element.type === 'multiple-choice';

  const handleOptionClick = (optionValue: string) => {
    const isCurrentlySelected = selectedValues.includes(optionValue);
    if (isMultipleChoice) {
      let newValues: string[];
      if (isCurrentlySelected) {
        newValues = selectedValues.filter((val) => val !== optionValue);
      } else {
        newValues = [...selectedValues, optionValue];
      }
      setSelectedValues(newValues);
      onChange(newValues);
      return;
    }
    if (isCurrentlySelected) {
      setSelectedValues([]);
      onChange('');
      return;
    }
    setSelectedValues([optionValue]);
    onChange(optionValue);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <label htmlFor={custom_field_id} className="font-medium text-sm text-gray-900">
          {name}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
        {description && <p className="text-xs text-gray-700 font-normal">{description}</p>}
      </div>

      <div className="flex flex-col gap-2">
        {options.map((option) => {
          const isSelected = selectedValues.includes(option.value);

          return (
            <button
              key={option.id}
              onClick={() => handleOptionClick(option.value)}
              className={cn(
                'flex flex-row gap-3 items-center justify-between p-3 rounded cursor-pointer transition-colors text-left w-full',
                isSelected ? 'bg-gray-100' : 'hover:bg-gray-50',
              )}
              type="button"
            >
              <span className={cn('text-sm', isSelected ? 'text-gray-900 font-medium' : 'text-gray-700 font-normal')}>
                {option.value}
              </span>
              {isSelected && <Icon name="check" className="w-4 h-4 text-gray-900" type="check" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
